import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getResourceIRI, getResourceUuid, type IClassroom, type IFrameworkItem, useFrameworkListQuery } from "@sol/sdk";
import { Select, type SelectProps } from "@sol/uikit/data-entry/Select";

import { FrameworkIdentity } from "./FrameworkIdentity";
import { toFrameworkLabel } from "../lib/frameworkLabel";

export const FrameworkSelect = ({
    onSelect,
    disabled = false,
    defaultValue,
    value,
    className,
    placeholder,
    size = "middle",
    mode = undefined,
    activeClassroom,
    getOptionLabel = toFrameworkLabel,
    ...rest
}: SelectProps & { activeClassroom?: IClassroom; getOptionLabel?: (framework: IFrameworkItem) => string }) => {
    const [t] = useTranslation();
    const usesFrameworkIdentity = getOptionLabel === toFrameworkLabel;
    const { data, isLoading } = useFrameworkListQuery({
        filters: {
            classrooms: activeClassroom ? getResourceUuid(activeClassroom) : undefined,
        },
        pagination: false,
    });
    const options = useMemo(
        () =>
            data?.["hydra:member"].map(framework => {
                return {
                    label: getOptionLabel(framework),
                    value: getResourceIRI(framework),
                    framework,
                };
            }) ?? [],
        [data, getOptionLabel],
    );

    return (
        <Select
            {...rest}
            options={options}
            disabled={disabled}
            className={className}
            showSearch
            virtual={false}
            defaultValue={value ?? defaultValue}
            value={value}
            onChange={onSelect}
            loading={isLoading}
            size={size}
            placeholder={placeholder ?? t("entities.framework.components.FrameworkSelect.placeholder")}
            mode={mode}
            optionRender={({ data: option }) => {
                const framework = option.framework as IFrameworkItem | undefined;

                return framework && usesFrameworkIdentity ? <FrameworkIdentity {...framework} /> : option.label;
            }}
            filterOption={(input, option) =>
                String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
            }
        />
    );
};
